import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { fetchFieldReportPhoto } from '../../services/intelPacketApi';
import {
  SubjectAssociation,
  SubjectCard,
  SubjectCardPatch,
  SubjectIndicator,
  exportSubjectCard,
  updateSubjectCard,
} from '../../services/subjectCardApi';
import { formatWhen } from '../missions/format';
import { metalSx } from '../../theme/metalButtons';

const GOLD = '#f5b942';
const INK = '#111318';

const THREATS = ['clear', 'watch', 'elevated', 'high', 'critical'] as const;
const STATUSES = ['active', 'inactive', 'archived'] as const;

export type SubjectScope = { missionId: number } | { packetId: number };

const threatColor: Record<string, string> = {
  clear: '#66bb6a',
  watch: GOLD,
  elevated: '#ef9a36',
  high: '#e57373',
  critical: '#ef5350',
};

function slug(alias: string): string {
  return alias.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'subject';
}

/**
 * One object URL per report, owned above the card views. Fetched here rather
 * than inside a view so toggling Card/Quad neither refetches nor drops the
 * picture. The URL is session-cached — do not revoke it.
 */
export function usePhotoUrl(reportId?: string | null): string | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!reportId) {
      setSrc(null);
      return undefined;
    }
    let cancelled = false;
    fetchFieldReportPhoto(reportId, 'original')
      .then((next) => { if (!cancelled) setSrc(next); })
      .catch(() => { if (!cancelled) setSrc(null); });
    return () => {
      cancelled = true;
    };
  }, [reportId]);
  return src;
}

const Photo: React.FC<{ src: string | null; alt: string }> = ({ src, alt }) => {
  if (!src) {
    return (
      <Box sx={{
        width: '100%', height: '100%',
        bgcolor: 'rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', color: 'text.secondary', fontSize: '0.62rem',
      }}>
        No photo
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
    />
  );
};

const Cell: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{
    border: '1px solid rgba(245,185,66,0.28)',
    bgcolor: 'rgba(17,19,24,0.92)',
    p: 1.1,
    minHeight: 92,
  }}>
    <Typography sx={{
      fontSize: '0.62rem', letterSpacing: 1.1, fontWeight: 800,
      color: GOLD, mb: 0.6,
    }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const Line: React.FC<{ children: React.ReactNode; muted?: boolean }> = ({ children, muted }) => (
  <Typography variant="caption" sx={{ display: 'block', color: muted ? 'text.secondary' : 'text.primary' }}>
    {children}
  </Typography>
);

export const SubjectQuad: React.FC<{
  card: SubjectCard;
  photoSrc?: string | null;
}> = ({ card, photoSrc = null }) => {
  const areas = card.derived?.areas || card.locations || [];
  const assoc = card.associations || [];
  const byType = assoc.reduce<Record<string, number>>((acc, row) => {
    const key = row.type || 'entity';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
      <Cell title="Who">
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
          {card.photo_report_id && (
            <Box sx={{ width: 56, height: 72, flexShrink: 0, overflow: 'hidden', bgcolor: '#000' }}>
              <Photo src={photoSrc} alt={card.alias} />
            </Box>
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Line>{card.alias}</Line>
            <Line muted>{(card.subject_type || 'person').replace('_', ' ')}</Line>
            <Line muted>
              {(card.threat_level || 'watch').toUpperCase()}
              {card.risk_score != null ? ` · ${card.risk_score}/100` : ''}
            </Line>
            <Line muted>{card.sighting_count} sightings · {card.status}</Line>
          </Box>
        </Box>
      </Cell>
      <Cell title="Where">
        <Line>{areas[0] || 'No location yet'}</Line>
        {areas.slice(1, 3).map((a) => <Line key={a} muted>{a}</Line>)}
        <Line muted>Last seen {card.last_seen ? formatWhen(card.last_seen) : '—'}</Line>
      </Cell>
      <Cell title="Network">
        <Line>{assoc.length} associated</Line>
        {Object.entries(byType).slice(0, 3).map(([k, n]) => (
          <Line key={k} muted>{n} {k}{n === 1 ? '' : 's'}</Line>
        ))}
        {assoc.length === 0 && <Line muted>No links recorded</Line>}
      </Cell>
      <Cell title="Assessment">
        <Line>{(card.threat_level || 'watch').replace('_', ' ')}</Line>
        <Line muted>
          {card.assessment?.trim() || 'Requires analyst review'}
        </Line>
      </Cell>
    </Box>
  );
};

const Flag: React.FC<{ item: SubjectIndicator }> = ({ item }) => {
  const color = item.kind === 'positive' ? '#66bb6a' : item.kind === 'warning' ? GOLD : '#90caf9';
  return (
    <Typography variant="caption" sx={{ display: 'block', color }}>
      {item.kind === 'warning' ? '⚠' : item.kind === 'positive' ? '✓' : '·'} {item.label}
    </Typography>
  );
};

export const SubjectBaseballCard: React.FC<{
  card: SubjectCard;
  photoSrc?: string | null;
}> = ({ card, photoSrc = null }) => {
  const areas = card.derived?.areas || card.locations || [];
  return (
    <Box sx={{ bgcolor: INK, color: '#eee', border: `1px solid ${GOLD}55` }}>
      <Box sx={{ display: 'flex', gap: 1.25, p: 1.25, borderBottom: `1px solid ${GOLD}33` }}>
        <Box sx={{ width: 72, height: 90, flexShrink: 0, overflow: 'hidden', bgcolor: '#000' }}>
          <Photo src={photoSrc} alt={card.alias} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: GOLD, fontWeight: 800, letterSpacing: 0.4, mb: 0.4 }}>
            {card.alias}
          </Typography>
          <Line muted>Type: {(card.subject_type || 'person').replace('_', ' ')}</Line>
          <Line>
            Risk:{' '}
            <Box component="span" sx={{ color: threatColor[card.threat_level] || GOLD, fontWeight: 700 }}>
              {(card.threat_level || 'watch').toUpperCase()}
              {card.risk_score != null ? ` ${card.risk_score}/100` : ''}
            </Box>
          </Line>
          <Line muted>Status: {card.status}</Line>
          <Line muted>First observed: {card.first_seen ? formatWhen(card.first_seen) : '—'}</Line>
          <Line muted>Last seen: {card.last_seen ? formatWhen(card.last_seen) : '—'}</Line>
          <Line muted>{card.sighting_count} sightings</Line>
        </Box>
      </Box>
      <Box sx={{ p: 1.25 }}>
        <Typography sx={{ color: GOLD, fontSize: '0.65rem', fontWeight: 800, letterSpacing: 0.8, mb: 0.4 }}>
          LOCATION ACTIVITY
        </Typography>
        <Line>{areas.length ? areas.join(' · ') : 'No geotagged sightings yet'}</Line>
        <Typography sx={{ color: GOLD, fontSize: '0.65rem', fontWeight: 800, letterSpacing: 0.8, mt: 1.1, mb: 0.4 }}>
          ASSOCIATED ENTITIES
        </Typography>
        {(card.associations || []).length === 0 ? (
          <Line muted>None recorded</Line>
        ) : card.associations.map((a) => (
          <Line key={`${a.type}-${a.name}`}>
            {a.type} · {a.name}{a.confidence != null ? ` · ${a.confidence}%` : ''}
          </Line>
        ))}
        <Typography sx={{ color: GOLD, fontSize: '0.65rem', fontWeight: 800, letterSpacing: 0.8, mt: 1.1, mb: 0.4 }}>
          PRIORITY INDICATORS
        </Typography>
        {(card.indicators || []).length === 0
          ? <Line muted>None</Line>
          : card.indicators.map((i) => <Flag key={i.label} item={i} />)}
        {card.notes?.trim() && (
          <>
            <Typography sx={{ color: GOLD, fontSize: '0.65rem', fontWeight: 800, letterSpacing: 0.8, mt: 1.1, mb: 0.4 }}>
              ANALYST NOTES
            </Typography>
            <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>{card.notes}</Typography>
          </>
        )}
        {card.assessment?.trim() && (
          <>
            <Typography sx={{ color: GOLD, fontSize: '0.65rem', fontWeight: 800, letterSpacing: 0.8, mt: 1.1, mb: 0.4 }}>
              ASSESSMENT
            </Typography>
            <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>{card.assessment}</Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

const fieldSx = {
  '& .MuiInputBase-input, & .MuiInputLabel-root, & .MuiSelect-select': { fontSize: '0.75rem' },
  '& .MuiInputLabel-root': { fontSize: '0.75rem' },
};

export const SubjectDossierDialog: React.FC<{
  open: boolean;
  card: SubjectCard | null;
  scope: SubjectScope;
  initialView?: 'baseball' | 'quad';
  onClose: () => void;
  onSaved: (card: SubjectCard) => void;
  onError: (message: string | null) => void;
}> = ({ open, card, scope, initialView = 'baseball', onClose, onSaved, onError }) => {
  const [view, setView] = useState<'baseball' | 'quad'>(initialView);
  const [draft, setDraft] = useState<SubjectCardPatch>({});
  const [assocText, setAssocText] = useState('');
  const [flagText, setFlagText] = useState('');
  const [extraKey, setExtraKey] = useState('');
  const [extraVal, setExtraVal] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // Fetched once per card, so switching Card/Quad keeps the same picture.
  const photoSrc = usePhotoUrl(open ? card?.photo_report_id : null);

  useEffect(() => {
    if (!card) return;
    setView(initialView);
    setDraft({
      alias: card.alias,
      notes: card.notes,
      assessment: card.assessment,
      threat_level: card.threat_level,
      risk_score: card.risk_score ?? undefined,
      status: card.status,
      indicators: card.indicators,
      associations: card.associations,
      extra: card.extra,
    });
  }, [card, initialView]);

  useEffect(() => {
    setAssocText('');
    setFlagText('');
    setSaveError(null);
    setSaved(false);
  }, [card?.id, initialView]);

  if (!card) return null;
  const live: SubjectCard = { ...card, ...draft, extra: draft.extra || card.extra };

  const save = async (patch: SubjectCardPatch = draft) => {
    setSaving(true);
    setSaved(false);
    setSaveError(null);
    onError(null);
    try {
      onSaved(await updateSubjectCard(scope, card.id, patch));
      setSaved(true);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((item: { msg?: string }) => item.msg).filter(Boolean).join('; ')
          : (err instanceof Error ? err.message : 'Could not save subject card');
      setSaveError(message || 'Could not save subject card');
      onError(message || 'Could not save subject card');
    } finally {
      setSaving(false);
    }
  };

  const addAssociation = () => {
    const [type, name, conf] = assocText.split('|').map((s) => s.trim());
    if (!name && !type) return;
    const next: SubjectAssociation[] = [
      ...(draft.associations || card.associations || []),
      {
        type: type || 'entity',
        name: name || type,
        confidence: conf ? Number(conf) : null,
      },
    ];
    setDraft((d) => ({ ...d, associations: next }));
    setAssocText('');
  };

  const addFlag = () => {
    const label = flagText.trim();
    if (!label) return;
    const next: SubjectIndicator[] = [
      ...(draft.indicators || []).filter((i) => i.source === 'analyst'),
      { label, kind: 'watch', source: 'analyst' },
    ];
    setDraft((d) => ({ ...d, indicators: next }));
    setFlagText('');
  };

  const addExtra = () => {
    const key = extraKey.trim();
    if (!key) return;
    setDraft((d) => ({ ...d, extra: { ...(d.extra || {}), [key]: extraVal } }));
    setExtraKey('');
    setExtraVal('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, pt: 1, gap: 1 }}>
        <Typography sx={{ fontWeight: 800, flex: 1 }}>{live.alias}</Typography>
        <Button size="small" onClick={() => setView((v) => (v === 'baseball' ? 'quad' : 'baseball'))}>
          {view === 'baseball' ? 'Quad' : 'Card'}
        </Button>
        <Tooltip title="Export HTML">
          <IconButton
            size="small"
            onClick={() => exportSubjectCard(scope, card.id, 'html', slug(live.alias)).catch((e) => onError(String(e)))}
          >
            <FileDownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>
      <DialogContent sx={{ pt: 1 }}>
        {view === 'quad'
          ? <SubjectQuad card={live} photoSrc={photoSrc} />
          : <SubjectBaseballCard card={live} photoSrc={photoSrc} />}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1.5 }}>
          <TextField
            label="Alias" size="small" value={draft.alias || ''}
            onChange={(e) => setDraft((d) => ({ ...d, alias: e.target.value }))}
            sx={fieldSx}
          />
          <TextField
            label="Risk 0–100" size="small" type="number"
            value={draft.risk_score ?? ''}
            onChange={(e) => setDraft((d) => ({
              ...d, risk_score: e.target.value === '' ? undefined : Number(e.target.value),
            }))}
            sx={fieldSx}
          />
          <TextField
            select label="Threat" size="small" value={draft.threat_level || 'watch'}
            onChange={(e) => setDraft((d) => ({ ...d, threat_level: e.target.value as SubjectCard['threat_level'] }))}
            sx={fieldSx}
          >
            {THREATS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField
            select label="Status" size="small" value={draft.status || 'active'}
            onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value as SubjectCard['status'] }))}
            sx={fieldSx}
          >
            {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
        </Box>
        <TextField
          label="Analyst notes" size="small" multiline minRows={2} fullWidth
          value={draft.notes || ''} sx={{ ...fieldSx, mt: 1 }}
          onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
        />
        <TextField
          label="Assessment" size="small" multiline minRows={2} fullWidth
          value={draft.assessment || ''} sx={{ ...fieldSx, mt: 1 }}
          onChange={(e) => setDraft((d) => ({ ...d, assessment: e.target.value }))}
        />
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <TextField
            size="small" fullWidth
            label="Link type|name|confidence"
            placeholder="vehicle|FL ABC123|69"
            value={assocText}
            onChange={(e) => setAssocText(e.target.value)}
            sx={fieldSx}
          />
          <Button size="small" onClick={addAssociation}>Add</Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <TextField
            size="small" fullWidth label="Analyst indicator"
            value={flagText} onChange={(e) => setFlagText(e.target.value)} sx={fieldSx}
          />
          <Button size="small" onClick={addFlag}>Flag</Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <TextField size="small" label="Field" value={extraKey} onChange={(e) => setExtraKey(e.target.value)} sx={fieldSx} />
          <TextField size="small" label="Value" value={extraVal} onChange={(e) => setExtraVal(e.target.value)} sx={{ ...fieldSx, flex: 1 }} />
          <Button size="small" onClick={addExtra}>Add</Button>
        </Box>
        {Object.entries(live.extra || {}).map(([k, v]) => (
          <Typography key={k} variant="caption" sx={{ display: 'block', mt: 0.4 }}>{k}: {v}</Typography>
        ))}
        {saveError && (
          <Typography sx={{ mt: 1, fontSize: '0.7rem', color: '#ef5350', fontWeight: 600 }}>
            {saveError}
          </Typography>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 1.5 }}>
          {saved && !saveError && (
            <Typography sx={{ fontSize: '0.7rem', color: '#66bb6a', fontWeight: 700 }}>
              Saved
            </Typography>
          )}
          <Button
            variant="contained" size="small" disabled={saving}
            onClick={() => save()}
            sx={metalSx('steel')}
          >
            {saving ? 'Saving…' : 'Save card'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
